import { useId } from 'react';
import { useI18n } from '../i18n';
import {
  createEmptyProfileVehicle,
  formatIsraelLicenseInput,
  formatProfileVehicleSummaryLine,
  PROFILE_VEHICLES_MAX,
  setProfileVehiclePrimary,
  type ProfileVehicle,
} from '../utils/profileVehicles';
import styles from './ProfileMenu.module.css';

type ProfileVehiclesEditSectionProps = {
  vehicles: ProfileVehicle[];
  onChange: (vehicles: ProfileVehicle[]) => void;
  disabled?: boolean;
  /** Optional cancel control (e.g. parking booking edit-list sheet). */
  onCancel?: () => void;
  cancelAriaLabel?: string;
};

export function ProfileVehiclesEditSection({
  vehicles,
  onChange,
  disabled = false,
  onCancel,
  cancelAriaLabel,
}: ProfileVehiclesEditSectionProps) {
  const { t } = useI18n();
  const sectionId = useId();

  const updateCar = (id: string, patch: Partial<ProfileVehicle>) => {
    onChange(vehicles.map((car) => (car.id === id ? { ...car, ...patch } : car)));
  };

  const addCar = () => {
    if (vehicles.length >= PROFILE_VEHICLES_MAX) {
      return;
    }
    onChange([...vehicles, createEmptyProfileVehicle(vehicles.length === 0)]);
  };

  const removeCar = (id: string) => {
    const next = vehicles.filter((car) => car.id !== id);
    if (next.length === 0) {
      onChange([]);
      return;
    }
    const hadPrimary = vehicles.find((car) => car.id === id)?.isPrimary;
    onChange(
      hadPrimary
        ? next.map((car, index) => ({ ...car, isPrimary: index === 0 }))
        : next,
    );
  };

  return (
    <section className={styles.profileEditVehiclesSection} aria-labelledby={`${sectionId}-heading`}>
      <div className={styles.profileEditVehiclesHeader}>
        <div className={styles.profileEditVehiclesHeaderText}>
          <h3 id={`${sectionId}-heading`} className={styles.profileEditPasswordTitle}>
            {t('profile.vehicles.title')}
          </h3>
          <p className={styles.profileEditPasswordOptional}>{t('profile.vehicles.optionalHint')}</p>
        </div>
        {onCancel ? (
          <button
            type="button"
            className={styles.profileEditVehiclesCancelBtn}
            onClick={onCancel}
            disabled={disabled}
            aria-label={cancelAriaLabel ?? t('common.cancel')}
          >
            ×
          </button>
        ) : null}
      </div>

      {vehicles.length === 0 ? (
        <p className={styles.profileEditVehiclesEmpty}>{t('profile.vehicles.empty')}</p>
      ) : (
        <ul className={styles.profileEditVehiclesList}>
          {vehicles.map((car, index) => {
            const headingId = `${sectionId}-car-${car.id}`;
            return (
              <li key={car.id} className={styles.profileEditVehicleCard}>
                <div className={styles.profileEditVehicleCardTop}>
                  <p id={headingId} className={styles.profileEditVehicleCardTitle}>
                    {t('profile.vehicles.carN', { n: String(index + 1) })}
                    {car.isPrimary ? ` · ${t('profile.vehicles.primary')}` : ''}
                  </p>
                  <button
                    type="button"
                    className={styles.profileEditVehicleRemoveBtn}
                    onClick={() => removeCar(car.id)}
                    disabled={disabled}
                  >
                    {t('profile.vehicles.remove')}
                  </button>
                </div>

                <div className={styles.profileEditField}>
                  <label className={styles.profileEditLabel} htmlFor={`${headingId}-make`}>
                    {t('profile.vehicles.manufacturer')}
                  </label>
                  <input
                    id={`${headingId}-make`}
                    className={styles.profileEditInput}
                    value={car.manufacturer}
                    disabled={disabled}
                    onChange={(event) => updateCar(car.id, { manufacturer: event.target.value })}
                    autoComplete="organization"
                  />
                </div>
                <div className={styles.profileEditField}>
                  <label className={styles.profileEditLabel} htmlFor={`${headingId}-model`}>
                    {t('profile.vehicles.model')}
                  </label>
                  <input
                    id={`${headingId}-model`}
                    className={styles.profileEditInput}
                    value={car.model}
                    disabled={disabled}
                    onChange={(event) => updateCar(car.id, { model: event.target.value })}
                  />
                </div>
                <div className={styles.profileEditField}>
                  <label className={styles.profileEditLabel} htmlFor={`${headingId}-color`}>
                    {t('profile.vehicles.color')}
                  </label>
                  <input
                    id={`${headingId}-color`}
                    className={styles.profileEditInput}
                    value={car.color}
                    disabled={disabled}
                    onChange={(event) => updateCar(car.id, { color: event.target.value })}
                  />
                </div>
                <div className={styles.profileEditField}>
                  <label className={styles.profileEditLabel} htmlFor={`${headingId}-plate`}>
                    {t('profile.vehicles.licenseNumber')}
                  </label>
                  <input
                    id={`${headingId}-plate`}
                    className={[styles.profileEditInput, styles.profileEditInputLtr].join(' ')}
                    dir="ltr"
                    inputMode="numeric"
                    autoComplete="off"
                    value={car.licenseNumber}
                    disabled={disabled}
                    onChange={(event) =>
                      updateCar(car.id, { licenseNumber: formatIsraelLicenseInput(event.target.value) })
                    }
                    placeholder={t('profile.vehicles.licensePlaceholder')}
                  />
                </div>
                <label className={styles.profileEditVehiclePrimary}>
                  <input
                    type="radio"
                    name={`${sectionId}-primary`}
                    checked={car.isPrimary}
                    disabled={disabled}
                    onChange={() => onChange(setProfileVehiclePrimary(vehicles, car.id))}
                  />
                  <span>{t('profile.vehicles.setPrimary')}</span>
                </label>
                {formatProfileVehicleSummaryLine(car) ? (
                  <p className={styles.profileEditVehiclePreview} dir="ltr">
                    {formatProfileVehicleSummaryLine(car)}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {vehicles.length < PROFILE_VEHICLES_MAX ? (
        <button
          type="button"
          className={styles.profileEditVehicleAddBtn}
          onClick={addCar}
          disabled={disabled}
        >
          {t('profile.vehicles.add')}
        </button>
      ) : (
        <p className={styles.profileEditVehiclesLimit}>{t('profile.vehicles.maxReached')}</p>
      )}
    </section>
  );
}
